import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './DigitalMenu.css';
import { FaSearch, FaChevronLeft, FaChevronRight, FaShoppingCart, FaUser } from 'react-icons/fa';
import { useParams } from "react-router-dom";
import { currencySymbolLeft } from '../../../functions/Functions';
import { BASE_URL } from '../../../BaseUrl';
import { useDigitalMenuLogics } from './useDigitalMenuLogics';
import InstructionModal from './components/InstructionModal';
import MenuItem from './components/MenuItem';
import CartSidebar from './components/CartSidebar';
import DigitalMenuItemModal from './components/DigitalMenuItemModal';
import PopularItemCard from './components/PopularItemCard';
import CustomSpinLoader from '../../components/CustomSpinLoader';
import ViewOrderModal from './components/ViewOrderModal';
import CustomerInfoModal from './components/CustomerInfoModal';
import BranchConfirmModal from './components/BranchConfirmModal';
import CardPaymentModal from './components/CardPaymentModal';
import DigitalMenuHeader from './components/DigitalMenuHeader';
import { ToastError } from '../../../functions/ToastShow';
import { Helmet } from 'react-helmet';

const DigitalMenu = () => {
  const { id, tableName, orderType } = useParams();
  
  // Use the custom hook for all logics
  const {
    orderDetail,
    loading,
    cartItems,
    cartSubTotal,
    cartTax,
    cartTotal,
    handleAddToCart,
    handleRemoveFromCart,
    handleUpdateInstruction,
    isCartOpen,
    setIsCartOpen,
    instructionModalState,
    setInstructionModalState,
    handlePlaceOrder,
    menuData,
    popularItems,
    customerOrder,
    handleUpdateCustomerInfo,
    isCustomerModalOpen,
    setIsCustomerModalOpen,
    isBranchConfirmModalOpen,
    setIsBranchConfirmModalOpen,
    isCardPaymentModalOpen,
    cardPaymentStep,
    getPaymentTaxRate
  } = useDigitalMenuLogics(id, tableName, orderType);

  const [activeCategory, setActiveCategory] = useState('');
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [selectedItemForModal, setSelectedItemForModal] = useState(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isViewOrderModalOpen, setIsViewOrderModalOpen] = useState(false);

  const handleCloseItemModal = useCallback(() => {
    setIsItemModalOpen(false);
    setSelectedItemForModal(null);
  }, []);

  const handleCloseViewOrderModal = useCallback(() => setIsViewOrderModalOpen(false), []);
  const handleCloseCustomerModal = useCallback(() => setIsCustomerModalOpen(false), [setIsCustomerModalOpen]);
  const handleCloseInstructionModal = useCallback(() => setInstructionModalState({ isOpen: false, item: null }), []);
  const handleProceedBranchConfirm = useCallback(() => setIsBranchConfirmModalOpen(false), [setIsBranchConfirmModalOpen]);
  
  // Ultimate fix for Android Chrome pull-to-refresh: 
  // Global overflow-x: hidden on the body completely disables native pull-to-refresh in Chrome.
  // We override it here when DigitalMenu is mounted.
  useEffect(() => {
    // document.documentElement.classList.add('enable-pull-to-refresh');
    document.body.classList.add('enable-pull-to-refresh');
    
    return () => {
      // document.documentElement.classList.remove('enable-pull-to-refresh');
      document.body.classList.remove('enable-pull-to-refresh');
    };
  }, []);

  const isOrderPunchingEnabled = useMemo(() => {
    if (!orderDetail?.branch) return true;
    return !!((tableName || orderType) && orderDetail.branch.is_self_ordering);
  }, [tableName, orderType, orderDetail?.branch]);

  const handleItemClick = useCallback((item) => {
    if (item.has_variation === "1" || item.has_variation === 1 || item.has_property === "1" || item.has_property === 1) {
      setSelectedItemForModal(item);
      setIsItemModalOpen(true);
    } else {
      if (!isOrderPunchingEnabled) {
        if ((tableName || orderType) && !tableName?.includes("M-")) {
          ToastError({ title: "Branch is not enabled for self ordering", position: "top-right", toastId: "self-ordering-error" });
        }
        return;
      }
      handleAddToCart(item);
    }
  }, [isOrderPunchingEnabled, tableName, orderType, handleAddToCart]);

  useEffect(() => {
    if (menuData.length > 0) {
      setActiveCategory(menuData[0].id);
    }
  }, [menuData]);

  const categoryContainerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const itemRefs = useRef({});
  const isClickScrolling = useRef(false);

  // Ultimate optimization: Debounce search to prevent rapid recalculations on 6k+ items
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 200); 
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Optimized filtering
  const filteredMenuData = useMemo(() => {
    if (!debouncedSearchQuery) return menuData;
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return menuData.map(category => ({
      ...category,
      items: category.items.filter(item =>
        item.name.toLowerCase().includes(lowerQuery)
      )
    })).filter(category => category.items.length > 0); 
  }, [debouncedSearchQuery, menuData]);

  // Optimized scroll tracking with debounce to slow down auto-selection
  const scrollTimeoutRef = useRef(null);

  const handleScrollSpy = useCallback(() => {
    if (isClickScrolling.current || !scrollContainerRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      const scrollPosition = container.scrollTop;

      let currentActiveId = filteredMenuData[0]?.id;

      for (const category of filteredMenuData) {
        const el = itemRefs.current[category.id];
        if (el) {
          const topOffset = el.offsetTop - container.offsetTop;
          if (topOffset <= scrollPosition + 150) {
            currentActiveId = category.id;
          } else {
            break; 
          }
        }
      }

      if (currentActiveId) {
        setActiveCategory(prev => {
          if (prev !== currentActiveId) {
            scrollToCategoryTab(currentActiveId);
            return currentActiveId;
          }
          return prev;
        });
      }
    }, 70); 
  }, [filteredMenuData]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScrollSpy, { passive: true });
      handleScrollSpy();
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScrollSpy);
    };
  }, [handleScrollSpy]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
    if (filteredMenuData.length > 0) {
      const firstId = filteredMenuData[0].id;
      setActiveCategory(firstId);
      scrollToCategoryTab(firstId);
    }
  }, [debouncedSearchQuery, filteredMenuData]);

  const handleCategoryScroll = () => {
    if (!categoryContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = categoryContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleCategoryScroll();
    }, 50);
    window.addEventListener('resize', handleCategoryScroll);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', handleCategoryScroll);
    };
  }, [filteredMenuData]);

  const scrollToCategoryTab = (id) => {
    if (!categoryContainerRef.current) return;
    const container = categoryContainerRef.current;
    const activeTab = container.querySelector(`[data-id="${id}"]`);

    if (activeTab) {
      const scrollLeft = activeTab.offsetLeft - container.offsetWidth / 2 + activeTab.offsetWidth / 2;
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  };

  const handleCategoryClick = (id) => {
    isClickScrolling.current = true;
    setActiveCategory(id);

    const element = itemRefs.current[id];
    const container = scrollContainerRef.current;

    if (element && container) {
      const topOffset = element.offsetTop - container.offsetTop;
      container.scrollTo({ top: topOffset, behavior: 'smooth' });
      scrollToCategoryTab(id);

      setTimeout(() => {
        isClickScrolling.current = false;
      }, 800);
    }
  };

  const scrollCategories = (direction) => {
    if (categoryContainerRef.current) {
      const scrollAmount = 300;
      categoryContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const hasTerms = Boolean(
    orderDetail?.terms_conditions &&
    String(orderDetail.terms_conditions).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
  );

  const viewBanner = hasTerms || orderDetail.header_text;

  const footerDescContent = orderDetail?.footer_desc || orderDetail?.footer_description || "";
  const hasFooterDesc = Boolean(
    footerDescContent &&
    String(footerDescContent).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
  );

  return (
    <>
      <Helmet>
        <title>Digital Menu</title>
      </Helmet>
      <div className="digital-menu-wrapper">
        <CustomSpinLoader loading={loading && !isCardPaymentModalOpen}/>
        {/* Unified Sleek Top Section */}
        <div className="menu-header-section bg-white shadow-sm position-relative">
          <div className="container-fluid">

            {/* Unified Logo, Search, and Action Header */}
            <DigitalMenuHeader
              searchQuery={searchQuery}
              isOrderPunchingEnabled= {isOrderPunchingEnabled}
              setSearchQuery={setSearchQuery}
              orderDetail={orderDetail}
              setIsCustomerModalOpen={setIsCustomerModalOpen}
              cartItems={cartItems}
              setIsCartOpen={setIsCartOpen}
            />

            {/* Category Scroller */}
            <div className="category-carousel-wrapper position-relative mx-auto mt-1">
              {showLeftArrow && (
                <button className="category-arrow arrow-left" onClick={() => scrollCategories('left')}>
                  <FaChevronLeft size={14} />
                </button>
              )}

              <div
                className="category-scroll-container px-2"
                ref={categoryContainerRef}
                onScroll={handleCategoryScroll}
              >
                <ul className="nav flex-nowrap align-items-center mb-0 pb-2 gap-2">
                  {menuData.map(category => (
                    <li className="nav-item" key={category.id}>
                      <button
                        data-id={category.id}
                        className={`category-pill ${activeCategory === category.id ? 'active' : ''}`}
                        onClick={() => handleCategoryClick(category.id)}
                      >
                        {category.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {showRightArrow && (
                <button className="category-arrow arrow-right" onClick={() => scrollCategories('right')}>
                  <FaChevronRight size={14} />
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Optimized Scrollable Items Section */}
        <div className="menu-items-scroll-container" ref={scrollContainerRef}>
          <div className="p-4 max-w-1500 mx-auto w-100">
            
            {/* Promotional Banner */}
            {
              viewBanner && (
                <div className="mb-3 shadow-sm border rounded-xlg overflow-hidden bg-white">
                  {
                    orderDetail.header_text ? (
                    <h6 className="promo-bg promo-heading text-white text-center mb-0">
                      {orderDetail.header_text}
                    </h6>
                    ) : ""
                  }
                  <div className="d-flex flex-column flex-md-row">
                    {
                      orderDetail.link_text || orderDetail.links.length > 0 ? (
                        <div className={`${hasTerms ? 'promo-left' : 'w-100 p-3'} d-flex flex-column align-items-center justify-content-center`}>
                          <div className="text-slate text-center mb-2 promo-text" style={{fontWeight: "600"}}>
                            {orderDetail?.link_text ?? ""}
                          </div>
                          <div className="d-flex gap-2 justify-content-center">
                            {orderDetail.links.map((link, idx) => (
                              <a key={idx} href={link.link} target="_blank" rel="noopener noreferrer" className="btn promo-btn rounded-sm px-3 py-2 text-center">
                                {link.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : ""
                    }
                    {
                      hasTerms && (
                        <div className="flex-grow-1 pd-2 d-flex flex-column justify-content-center">
                          {/<[a-z][\s\S]*>/i.test(orderDetail.terms_conditions) ? (
                            <div
                              className="promo-terms-rich"
                              dangerouslySetInnerHTML={{ __html: orderDetail.terms_conditions }}
                            />
                          ) : (
                            <>
                              <h6 className="text-slate mb-2 promo-text">TERMS & CONDITIONS</h6>
                              <ul className="promo-list mb-0 promo-text">
                                {orderDetail.terms_conditions
                                  .split('\n')
                                  .filter(line => line.trim() !== '')
                                  .map((line, index) => (
                                    <li key={index}>{line}</li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>
                      )
                    }
                  </div>
                </div>
              )
            }

            {/* Previously Ordered Banner */}
            {customerOrder && (
              <div className="mb-3 prev-order-banner">
                <div className="d-flex align-items-center justify-content-between px-3 py-3">
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm prev-order-icon-box-sm">
                      <FaShoppingCart className="text-dark prev-order-icon-sm" />
                    </div>
                    <div>
                      <div className="text-white citem mb-0 prev-order-title">Your Order</div>
                      <div className="text-white-50 prev-order-subtitle">View your items</div>
                    </div>
                  </div>
                  <button 
                    className="btn prev-order-btn shadow-sm"
                    onClick={() => setIsViewOrderModalOpen(true)}
                  >
                    View Order
                  </button>
                </div>
                
                <div className="px-3 pb-3">
                  <div 
                    className="d-flex align-items-center prev-order-card" 
                    onClick={() => setIsViewOrderModalOpen(true)} 
                  >
                    <div className="d-flex align-items-center flex-grow-1">
                      {orderDetail?.branch?.dm_image_display && <div className="rounded overflow-hidden d-flex align-items-center justify-content-center prev-order-icon-box-lg" style={{ backgroundColor: '#f8f9fa' }}>
                        <img 
                          src={customerOrder.items?.[0]?.item_image ? BASE_URL + customerOrder.items[0].item_image : "/assets/img/default-item.jpeg"} 
                          alt="item" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                          onError={(e) => { e.target.onerror = null; e.target.src = "/assets/img/default-item.jpeg" }}
                        />
                      </div>}
                      <div>
                        <div className="mb-1 text-dark prev-order-item-title citem">
                          {customerOrder.items?.[0]?.item_name || 'Items'} 
                          {customerOrder.items?.length > 1 ? ` +${customerOrder.items.length - 1} more` : ''}
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="badge prev-order-badge">
                            {customerOrder.items?.length || 1} Items
                          </span>
                          <span className="prev-order-price">{currencySymbolLeft()} {customerOrder.total_payable}</span>
                        </div>
                      </div>
                    </div>
                    <FaChevronRight className="text-muted ms-2 prev-order-chevron" />
                  </div>
                </div>
              </div>
            )}
            
            {/* Popular Items Banner */}
            {popularItems && popularItems.length > 0 && !debouncedSearchQuery && (
              <div className="mb-4">
                <div className="popular-section-heading mb-3 px-1">
                  <span className="popular-title-icon" aria-hidden="true">🔥</span>
                  <div className="popular-title-copy">
                    <h5 className="popular-title">Popular Items</h5>
                    <div className="popular-subtitle">Most ordered right now</div>
                  </div>
                </div>
                <div className="d-flex overflow-auto pb-2 popular-scroll-container" style={{ gap: '12px' }}>
                  {popularItems.map(item => (
                    <PopularItemCard key={item.id} isOrderPunchingEnabled={isOrderPunchingEnabled} showImage={orderDetail?.branch?.dm_image_display} item={item} onClick={handleItemClick} />
                  ))}
                </div>
              </div>
            )}
            
            {!loading && filteredMenuData.length === 0 && (
              <div className="text-center py-5">
                <h4 className="empty-state-heading text-muted font-weight-bold">No items found</h4>
                <p className="empty-state-text text-secondary">Try searching for something else.</p>
              </div>
            )}

            {filteredMenuData.map((category,index) => (
              <div
                key={category.id}
                id={category.id}
                ref={el => itemRefs.current[category.id] = el}
                className={`menu-category-block ${
                  (index === filteredMenuData?.length - 1) ? "" : "mb-4"
                }`}
              >
                {/* Sticky Minimal Heading with Rich Shadow */}
                <div className="category-heading-sticky mb-0">
                  <div className="d-inline-flex flex-column">
                    <div className="d-flex align-items-baseline">
                      <h5 className="mb-0">{category.name}</h5>
                      <span className="category-item-count">({category.items?.length || 0})</span>
                    </div>
                    <div className="category-heading-underline"></div>
                  </div>
                </div>

                {/* Vertical Layout for Items - 5-6 per row on desktop, 2-3 on mobile */}
                <div className="row g-2 mt-0">
                  {category.items.map(item => (
                    <div className="col-6 col-md-4 col-lg-3 col-xl-2" key={item.name}>
                      <MenuItem 
                        item={item}
                        showImage={orderDetail?.branch?.dm_image_display}
                        isOrderPunchingEnabled= {isOrderPunchingEnabled}
                        cartItem={cartItems[item.id || item.name]} 
                        onAddToCart={handleItemClick} 
                        onRemoveFromCart={handleRemoveFromCart} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Modern Footer Description */}
            {hasFooterDesc && (
              <div className="dm-footer-desc-card">
                <div className="dm-footer-desc-body">
                  {/<[a-z][\s\S]*>/i.test(footerDescContent) ? (
                    <div
                      className="dm-footer-rich"
                      dangerouslySetInnerHTML={{ __html: footerDescContent }}
                    />
                  ) : (
                    <div className="dm-footer-plain">
                      {footerDescContent}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Digital Menu Footer */}
          {!loading && menuData.length > 0 && (
            <div className="digital-menu-footer">
              <span className="footer-text">Powered By</span>
              <span className="footer-brand">Indolj</span>
            </div>
          )}
        </div>

        {/* Floating Bottom Bar */}
        {Object.values(cartItems)?.length > 0 && (
          <div className="fixed-bottom d-flex justify-content-center px-3 floating-cart-wrapper" style={{paddingBottom:"2rem"}}>
            <button 
              className="btn text-white rounded-pill shadow-lg d-flex align-items-center justify-content-between px-4 py-3 w-100 floating-cart-btn"
              onClick={() => setIsCartOpen(true)}
            >
              <div className="d-flex align-items-center">
                <span className="bg-white text-dark rounded-circle cart-badge">{Object.values(cartItems)?.length ?? 0}</span>
                <span className="ml-2 cart-text-left">View Cart</span>
              </div>
              <div className="d-flex align-items-center cart-text-right">
                {currencySymbolLeft()} {cartTotal.toFixed(2)} <FaChevronRight className="ml-2 cart-chevron" />
              </div>
            </button>
          </div>
        )}

        {/* Cart Sidebar */}
        <CartSidebar 
          isCartOpen={isCartOpen}
          setIsCartOpen={setIsCartOpen}
          cartItems={cartItems}
          handleAddToCart={handleAddToCart}
          handleRemoveFromCart={handleRemoveFromCart}
          setInstructionModalState={setInstructionModalState}
          cartSubTotal={cartSubTotal}
          cartTax={cartTax}
          cartTotal={cartTotal}
          orderDetail={orderDetail}
          loading={loading}
          handlePlaceOrder={handlePlaceOrder}
          popularItems={popularItems}
          handleItemClick={handleItemClick}
          showImages={orderDetail?.branch?.dm_image_display}
          getPaymentTaxRate={getPaymentTaxRate}
        />
        
        {/* Backdrop for mobile */}
        <div 
          className={`cart-backdrop position-fixed w-100 ${isCartOpen ? 'open' : ''}`} 
          style={{ height: "100%", top: 0, left: 0, zIndex: 1045 }} 
          onClick={() => setIsCartOpen(false)}
        ></div>

        {/* Instruction Modal */}
        <InstructionModal 
          isOpen={instructionModalState.isOpen}
          onClose={handleCloseInstructionModal}
          cartItem={instructionModalState.item}
          onSaveInstruction={handleUpdateInstruction}
        />

        <DigitalMenuItemModal 
          isOpen={isItemModalOpen}
          onClose={handleCloseItemModal}
          item={selectedItemForModal}
          showImage={orderDetail.branch?.show_addon_image}
          onAddToCart={handleAddToCart}
          checkIsPunchingEnabled={isOrderPunchingEnabled}
        />

        <ViewOrderModal 
          isOpen={isViewOrderModalOpen}
          onClose={handleCloseViewOrderModal}
          order={customerOrder}
          orderDetail={orderDetail}
          showImages={orderDetail?.branch?.dm_image_display}
        />

        <CustomerInfoModal
          isOpen={isCustomerModalOpen}
          onClose={handleCloseCustomerModal}
          customerInfo={orderDetail?.newCustomerInfo}
          onSave={handleUpdateCustomerInfo}
          branchId={orderDetail?.branch?.id}
        />

        <BranchConfirmModal
          isOpen={isBranchConfirmModalOpen}
          onProceed={handleProceedBranchConfirm}
          branchName={orderDetail?.branch?.name}
          tableName={orderDetail?.table?.name || (orderType ? "" : tableName)}
          branchId={orderDetail?.branch?.id}
        />

        <CardPaymentModal
          isOpen={isCardPaymentModalOpen}
          step={cardPaymentStep}
          amount={cartTotal}
        />
      </div>
    </>
  );
};

export default DigitalMenu;
